import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { Grid, Tabs, Tab } from '@nextiva/next-ui-components';
import { Page } from 'containers';
import { useStateWithLocalStorage } from 'utils/state-helpers';
import { TabPanel } from 'components';
import { getLocksForAllEntities } from 'services';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import DataImportTiles from './DataImportTiles';
import ImportData from './ImportData';
import HistoryLog from './HistoryLog';
import { DataImportContext } from './context';
import { BodyGrid } from './DataImport.styles';

export const screens = {
  USERS_IMPORT: 'USER',
  TEAMS_IMPORT: 'TEAM',
  LOCATIONS_IMPORT: 'LOCATION',
  PHONES_AND_DEVICES_IMPORT: 'DEVICE',
  USER_UPDATE: 'USER_UPDATE',
};

const DataImport = () => {
  const [importType, setImportType] = useState('');
  const location = useLocation();
  const [tabIndex, setTabIndex] = useStateWithLocalStorage(
    'dataImportTabIndex',
    0,
  );
  const { corpAcctNbr } = useContext(FeatureContext);

  const { data, refetch: refetchJobLocks } = useQuery('jobLocks', () =>
    getLocksForAllEntities(corpAcctNbr),
  );

  const updateJobLocks = () => {
    refetchJobLocks().catch(() => null);
  };

  useEffect(() => {
    if (location.state) {
      setImportType(location.state);
      setTabIndex(0);
    }
    if (location.hash) {
      setImportType(location.hash.replace('#', ''));
    }
  }, [location.state, location.hash]);

  const handleTabChange = useCallback(
    (_event, newValue: number) => {
      setTabIndex(newValue);
      setImportType('');
      updateJobLocks();
    },
    [setTabIndex],
  );

  const goToImportPage = () => {
    setTabIndex(0);
    setImportType('');
    updateJobLocks();
  };

  const goToPage = (page) => {
    setTabIndex(0);
    setImportType(page);
    updateJobLocks();
  };

  const dataContext = useMemo(
    () => ({
      handleTabChange,
      goToImportPage,
      goToPage,
      jobLocks: data?.data,
      refetchJobLocks,
    }),
    [handleTabChange, data?.data],
  );

  return (
    <DataImportContext.Provider value={dataContext}>
      <Page title="Data import" isTabs>
        <Grid container>
          <Grid item xs={12}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              data-testid="PlatformAdminUI-DataImport-Tabs"
            >
              <Tab
                label="Imports"
                data-testid="PlatformAdminUI-DataImport-Imports"
              />
              <Tab
                label="History"
                data-testid="PlatformAdminUI-DataImport-History"
              />
            </Tabs>
          </Grid>
          <BodyGrid item xs={12}>
            <TabPanel value={tabIndex} index={0}>
              {importType ? (
                <ImportData
                  importType={importType}
                  importHandler={goToImportPage}
                />
              ) : (
                <DataImportTiles setStartImport={setImportType} />
              )}
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              <HistoryLog />
            </TabPanel>
          </BodyGrid>
        </Grid>
      </Page>
    </DataImportContext.Provider>
  );
};

export default DataImport;
